/**
 * Smart Sticky: bidirectional sticky behavior.
 *
 * When the element is shorter than the viewport → classic sticky top.
 * When taller:
 *   - Scrolling DOWN → the element scrolls naturally until its bottom
 *     reaches the viewport bottom, then sticks there.
 *   - Scrolling UP → the element scrolls naturally until its top
 *     reaches the viewport top, then sticks there.
 *
 * Works by toggling between position:sticky with dynamic top values
 * and position:relative with a computed translateY when "free-scrolling".
 */
(function () {
  const SELECTOR = '.product__info-container';
  const DESKTOP_MIN = 990; // must match your CSS breakpoint

  function initSmartSticky() {
    const stickyElement = document.querySelector(SELECTOR);
    if (!stickyElement) {
      return;
    }

    const wrapper = stickyElement.closest('.product__info-wrapper');
    if (!wrapper) {
      return;
    }

    let lastScrollY = window.scrollY;
    let direction = 'down'; // 'up' | 'down'
    let mode = 'top'; // 'top' | 'bottom' | 'free'
    let freeOffset = 0; // translateY value when in "free" mode
    let ticking = false;

    /**
     * Get the visible header height (sticky header offset).
     * Uses the CSS custom property --header-height set by the Shopify theme.
     */
    function getHeaderOffset() {
      const val = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      return val ? parseInt(val, 10) || 0 : 0;
    }

    function apply() {
      // Only active on desktop
      if (window.innerWidth < DESKTOP_MIN) {
        reset();
        return;
      }

      const headerOffset = getHeaderOffset();
      const gap = 24; // extra visual gap (≈1.5rem)
      const topOffset = headerOffset + gap;
      const viewportH = window.innerHeight;
      const elH = stickyElement.offsetHeight;

      // If element fits in viewport, use classic sticky top
      if (elH + topOffset <= viewportH) {
        stickyElement.style.position = 'sticky';
        stickyElement.style.top = topOffset + 'px';
        stickyElement.style.transform = '';
        mode = 'top';
        return;
      }

      const scrollY = window.scrollY;
      const scrollDelta = scrollY - lastScrollY;

      // Determine direction
      if (scrollDelta > 0) {
        direction = 'down';
      } else if (scrollDelta < 0) {
        direction = 'up';
      }

      // Wrapper rect gives us the reference frame
      const wrapperRect = wrapper.getBoundingClientRect();
      const stickyElementRect = stickyElement.getBoundingClientRect();

      // How far the element's top is from the wrapper's top (in page coords)
      const elTopInWrapper = stickyElementRect.top - wrapperRect.top;

      if (direction === 'down') {
        if (mode === 'top') {
          // Was stuck at top, now switching direction to down → go free
          // Calculate current offset to preserve visual position
          freeOffset = elTopInWrapper;
          stickyElement.style.position = 'relative';
          stickyElement.style.top = '';
          stickyElement.style.transform = 'translateY(' + freeOffset + 'px)';
          mode = 'free';
        }

        if (mode === 'free') {
          // Check if bottom of element reached bottom of viewport
          if (stickyElementRect.bottom <= viewportH) {
            // Stick to bottom
            const stickyTop = viewportH - elH;
            stickyElement.style.position = 'sticky';
            stickyElement.style.top = stickyTop + 'px';
            stickyElement.style.transform = '';
            mode = 'bottom';
          }
        }
        // If mode === 'bottom', stay stuck at bottom — nothing to do
      } else {
        // direction === 'up'
        if (mode === 'bottom') {
          // Was stuck at bottom, now switching direction to up → go free
          freeOffset = elTopInWrapper;
          stickyElement.style.position = 'relative';
          stickyElement.style.top = '';
          stickyElement.style.transform = 'translateY(' + freeOffset + 'px)';
          mode = 'free';
        }

        if (mode === 'free') {
          // Check if top of element reached top offset
          if (stickyElementRect.top >= topOffset) {
            // Stick to top
            stickyElement.style.position = 'sticky';
            stickyElement.style.top = topOffset + 'px';
            stickyElement.style.transform = '';
            mode = 'top';
          }
        }
        // If mode === 'top', stay stuck at top — nothing to do
      }

      lastScrollY = scrollY;
    }

    function reset() {
      stickyElement.style.position = '';
      stickyElement.style.top = '';
      stickyElement.style.transform = '';
      mode = 'top';
      freeOffset = 0;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          apply();
          ticking = false;
        });
      }
    }

    function onResize() {
      // Re-evaluate on resize
      lastScrollY = window.scrollY;
      mode = 'top';
      freeOffset = 0;
      apply();
    }

    /**
     * Called when the element's height changes (e.g. accordion open/close).
     * Re-evaluates sticky mode while trying to keep visual continuity.
     */
    function onHeightChange() {
      const headerOffset = getHeaderOffset();
      const gap = 24;
      const topOffset = headerOffset + gap;
      const viewportH = window.innerHeight;
      const elH = stickyElement.offsetHeight;

      // If it now fits, just stick to top
      if (elH + topOffset <= viewportH) {
        stickyElement.style.position = 'sticky';
        stickyElement.style.top = topOffset + 'px';
        stickyElement.style.transform = '';
        mode = 'top';
        return;
      }

      // If we were stuck at bottom, the bottom edge may now be wrong — recalc
      if (mode === 'bottom') {
        const stickyTop = viewportH - elH;
        stickyElement.style.position = 'sticky';
        stickyElement.style.top = stickyTop + 'px';
        stickyElement.style.transform = '';
        return;
      }

      // If free or top, check that visual position is still coherent
      if (mode === 'free') {
        const stickyElementRect = stickyElement.getBoundingClientRect();
        // If opening an accordion pushed the bottom below viewport, re-stick bottom
        if (stickyElementRect.bottom > viewportH && direction === 'down') {
          const stickyTop = viewportH - elH;
          stickyElement.style.position = 'sticky';
          stickyElement.style.top = stickyTop + 'px';
          stickyElement.style.transform = '';
          mode = 'bottom';
        }
      }
    }

    // Watch for size changes on the element (accordion open/close, dynamic content)
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      let lastHeight = stickyElement.offsetHeight;
      resizeObserver = new ResizeObserver(function (entries) {
        const newHeight = entries[0].contentRect.height;
        if (newHeight !== lastHeight) {
          lastHeight = newHeight;
          onHeightChange();
        }
      });
      resizeObserver.observe(stickyElement);
    } else {
      console.warn('[SmartSticky] ResizeObserver not supported');
    }

    // Also listen for <details> toggle events inside the element
    stickyElement.addEventListener('toggle', function (e) {
      // Small delay to let the browser layout update
      requestAnimationFrame(onHeightChange);
    }, true); // useCapture to catch toggle on nested <details>

    // Initialize
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });

    // First run
    apply();
  }

  // Run after DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSmartSticky);
  } else {
    initSmartSticky();
  }
})();
